import axios from 'axios';
import { Contact, ContactApiParams, ContactFilters } from '../types/contact.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await api.post(`${API_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('accessToken', access);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

class ContactService {
  // Contacts
  async getContacts(filters?: ContactFilters | ContactApiParams): Promise<PaginatedResponse<Contact>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }
    
    const response = await api.get<PaginatedResponse<Contact>>(`/contacts/?${params.toString()}`);
    return response.data;
  }

  async getContact(id: string): Promise<Contact> {
    const response = await api.get<Contact>(`/contacts/${id}/`);
    return response.data;
  }

  async createContact(data: FormData | any): Promise<Contact> {
    const isFormData = data instanceof FormData;
    const response = await api.post<Contact>('/contacts/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
    });
    return response.data;
  }

  async updateContact(id: string, data: FormData | any): Promise<Contact> {
    const isFormData = data instanceof FormData;
    const response = await api.patch<Contact>(`/contacts/${id}/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
    });
    return response.data;
  }

  async deleteContact(id: string): Promise<void> {
    await api.delete(`/contacts/${id}/`);
  }

  // Utility methods
  async exportContacts(filters?: ContactFilters): Promise<Blob> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }
    
    const response = await api.get(`/contacts/export/?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async importContacts(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/contacts/import_contacts/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async findDuplicates(): Promise<any[]> {
    const response = await api.get<any[]>('/contacts/duplicates/');
    return response.data;
  }
}

export default new ContactService();